/**
 * PlanetDragGestureHandler - Long press and drag detection for planets
 *
 * Handles:
 * - Long press detection (300ms)
 * - Pointer event tracking
 * - Touch and mouse support
 * - Drag delta calculation
 */

import { devLog } from '../utils/devLog';
import { getLocalStorageItem } from '../utils/localStorageUtils';
import { globalEventStream, createEvent } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import type { BaseEvent } from '../components/common/AstrologyChart/universal_architecture/types/EventTypes';
import { UI_TIMING } from '../config/timingConfig';

// =============================================================================
// Configuration
// =============================================================================

const LONG_PRESS_DELAY_MS = UI_TIMING.LONG_PRESS_DELAY;
const DRAG_THRESHOLD_PX = 5; // Minimum movement to start drag
const DEBUG = true; // Enable debug logging
const INDICATOR_STROKE_WIDTH = 3;
const INDICATOR_COLOR = '#FFD700'; // Gold color

// =============================================================================
// Types
// =============================================================================

interface GestureState {
  isActive: boolean;
  planet: string | null;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  element: SVGGElement | null;
  indicatorElement: SVGCircleElement | null;
}

// =============================================================================
// PlanetDragGestureHandler Class
// =============================================================================

export class PlanetDragGestureHandler {
  private chartId: string;
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private state: GestureState = {
    isActive: false,
    planet: null,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    element: null,
    indicatorElement: null,
  };
  private boundHandlers: {
    pointerDown: (e: PointerEvent) => void;
    pointerMove: (e: PointerEvent) => void;
    pointerUp: (e: PointerEvent) => void;
    pointerCancel: (e: PointerEvent) => void;
  };
  private isDestroyed = false;
  private isMotionActive = false; // Flag to prevent new drags while any motion (planet drag or time motion) is active
  private eventSubscriptions: Array<{ unsubscribe: () => void }> = [];

  constructor(chartId: string) {
    this.chartId = chartId;

    // Bind handlers once
    this.boundHandlers = {
      pointerDown: this.handlePointerDown.bind(this),
      pointerMove: this.handlePointerMove.bind(this),
      pointerUp: this.handlePointerUp.bind(this),
      pointerCancel: this.handlePointerCancel.bind(this),
    };

    if (DEBUG) devLog(`[PlanetDrag:Gesture] 🎯 Created for chart ${chartId}`);

    // Subscribe to drag state events to track when drag is active
    this.subscribeToDragState();

    // Attach handlers immediately - sprites should already exist (created after lines.ready)
    this.attachHandlersToSprites();
  }

  private subscribeToDragState() {
    // Listen to drag.ready (drag mode started) and drag.confirmed (drag mode ended)
    this.eventSubscriptions.push(
      globalEventStream.subscribe('planet.drag.ready', (event: BaseEvent) => {
        if (DEBUG) devLog(`[PlanetDrag:Gesture] 📥 Received planet.drag.ready, event chartId: ${event.metadata?.chartId}, my chartId: ${this.chartId}`);
        if (event.metadata?.chartId === this.chartId) {
          this.isMotionActive = true;
          if (DEBUG) devLog(`[PlanetDrag:Gesture] 🔒 Motion active (planet drag), ignoring new pointer events`);
        }
      })
    );

    // Also listen for confirmed/cancelled to reset
    this.eventSubscriptions.push(
      globalEventStream.subscribe('planet.drag.confirmed', (event: BaseEvent) => {
        if (DEBUG) devLog(`[PlanetDrag:Gesture] 📥 Received planet.drag.confirmed, event chartId: ${event.metadata?.chartId}, my chartId: ${this.chartId}`);
        if (event.metadata?.chartId === this.chartId) {
          this.isMotionActive = false;
          if (DEBUG) devLog(`[PlanetDrag:Gesture] 🔓 Motion ended (planet drag confirmed)`);
        }
      })
    );

    this.eventSubscriptions.push(
      globalEventStream.subscribe('planet.drag.cancel', (event: BaseEvent) => {
        if (event.metadata?.chartId === this.chartId) {
          this.isMotionActive = false;
          if (DEBUG) devLog(`[PlanetDrag:Gesture] 🔓 Motion ended (planet drag cancel)`);
        }
      })
    );

    // Listen to time motion overlay events
    this.eventSubscriptions.push(
      globalEventStream.subscribe('time.motion.overlay.opened', (event: BaseEvent) => {
        if (event.metadata?.chartId === this.chartId) {
          this.isMotionActive = true;
          if (DEBUG) devLog(`[PlanetDrag:Gesture] 🔒 Motion active (time motion), ignoring new pointer events`);
        }
      })
    );

    this.eventSubscriptions.push(
      globalEventStream.subscribe('time.motion.overlay.closed', (event: BaseEvent) => {
        if (event.metadata?.chartId === this.chartId) {
          this.isMotionActive = false;
          if (DEBUG) devLog(`[PlanetDrag:Gesture] 🔓 Motion ended (time motion closed)`);
        }
      })
    );

    // Re-attach handlers when sprites are recreated after chart re-render
    this.eventSubscriptions.push(
      globalEventStream.subscribe('lines.ready', (event: BaseEvent) => {
        if (event.metadata?.chartId === this.chartId) {
          if (DEBUG) devLog(`[PlanetDrag:Gesture] 🔄 lines.ready received, re-attaching handlers`);
          this.attachHandlersToSprites();
        }
      })
    );
  }

  // ===========================================================================
  // Initialization
  // ===========================================================================

  private attachHandlersToSprites() {
    if (this.isDestroyed) return;

    const sprites = this.getSprites();
    if (DEBUG) {
      devLog(`[PlanetDrag:Gesture] 📎 Found ${sprites.length} sprites for chart ${this.chartId}`);
    }

    // Remove old handlers first to prevent duplicates (uses same bound functions)
    sprites.forEach((sprite) => {
      sprite.removeEventListener('pointerdown', this.boundHandlers.pointerDown);
    });

    // Add new handlers
    sprites.forEach((sprite) => {
      sprite.addEventListener('pointerdown', this.boundHandlers.pointerDown);
    });
  }

  private getSprites(): NodeListOf<SVGGElement> {
    return document.querySelectorAll<SVGGElement>(
      `#planet-sprites-${this.chartId} .planet-sprite`
    );
  }

  // ===========ко================================================================
  // Pointer Event Handlers
  // ===========================================================================

  private handlePointerDown(e: PointerEvent) {
    if (this.isDestroyed) return;

    // Ignore new pointer events while any motion (planet drag or time motion) is active
    if (this.isMotionActive) {
      if (DEBUG) devLog(`[PlanetDrag:Gesture] 🚫 PointerDown ignored - motion active`);
      return;
    }

    const target = e.currentTarget as SVGGElement;
    const planet = target.dataset.planet;

    if (!planet) return;

    // Only allow drag for planets with direct motion (no retrograde)
    // Lg (Lagna), Ch (Chandra), Sy (Surya) always have direct motion
    const ALLOWED_PLANETS = ['Lg', 'Ch', 'Sy'];
    if (!ALLOWED_PLANETS.includes(planet)) {
      if (DEBUG) devLog(`[PlanetDrag:Gesture] 🚫 PointerDown ignored - ${planet} not in allowed list`);
      return;
    }

    if (DEBUG) devLog(`[PlanetDrag:Gesture] 👇 PointerDown on ${planet}`);

    // Capture pointer for tracking
    target.setPointerCapture(e.pointerId);

    // Store start position
    this.state = {
      isActive: false,
      planet,
      startX: e.clientX,
      startY: e.clientY,
      currentX: e.clientX,
      currentY: e.clientY,
      element: target,
      indicatorElement: null,
    };

    // Create visual indicator
    this.createIndicator(target);

    // Add move/up listeners to document for tracking outside element
    document.addEventListener('pointermove', this.boundHandlers.pointerMove);
    document.addEventListener('pointerup', this.boundHandlers.pointerUp);
    document.addEventListener('pointercancel', this.boundHandlers.pointerCancel);

    // Start long press timer
    this.longPressTimer = setTimeout(() => {
      this.activateDrag();
    }, LONG_PRESS_DELAY_MS);
  }

  private handlePointerMove(e: PointerEvent) {
    if (this.isDestroyed) return;
    if (!this.state.planet) return;

    this.state.currentX = e.clientX;
    this.state.currentY = e.clientY;

    // If not yet active, check if moved too much (cancel long press)
    if (!this.state.isActive) {
      const movedX = Math.abs(e.clientX - this.state.startX);
      const movedY = Math.abs(e.clientY - this.state.startY);

      if (movedX > DRAG_THRESHOLD_PX || movedY > DRAG_THRESHOLD_PX) {
        // Moved too much before long press completed - cancel
        this.cancelLongPress();
      }
      return;
    }

    // Active drag - emit move event
    const deltaX = e.clientX - this.state.startX;
    const deltaY = e.clientY - this.state.startY;

    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.gesture.move',
      'PlanetDragGestureHandler',
      {
        metadata: {
          chartId: this.chartId,
          planet: this.state.planet,
          deltaX,
          deltaY,
          clientX: e.clientX,
          clientY: e.clientY,
        },
      }
    ));
  }

  private handlePointerUp(e: PointerEvent) {
    if (this.isDestroyed) return;

    // Release pointer capture
    if (this.state.element) {
      try {
        this.state.element.releasePointerCapture(e.pointerId);
      } catch {
        // Ignore if already released
      }
    }

    if (this.state.isActive) {
      if (DEBUG) devLog(`[PlanetDrag:Gesture] 👆 PointerUp - emitting gesture.end for ${this.state.planet}`);
      // Was dragging - emit end event
      globalEventStream.emit(createEvent<BaseEvent>(
        'planet.drag.gesture.end',
        'PlanetDragGestureHandler',
        {
          metadata: {
            chartId: this.chartId,
            planet: this.state.planet,
          },
        }
      ));
    } else {
      if (DEBUG) devLog(`[PlanetDrag:Gesture] 👆 PointerUp - not active, no event`);
    }

    this.cleanup();
  }

  private handlePointerCancel(e: PointerEvent) {
    if (this.isDestroyed) return;

    if (this.state.isActive) {
      // Was dragging - emit cancel
      globalEventStream.emit(createEvent<BaseEvent>(
        'planet.drag.gesture.cancel',
        'PlanetDragGestureHandler',
        {
          metadata: {
            chartId: this.chartId,
            planet: this.state.planet,
          },
        }
      ));
    }

    this.cleanup();
  }

  // ===========================================================================
  // Drag Activation
  // ===========================================================================

  private activateDrag() {
    if (this.isDestroyed) return;
    if (!this.state.planet) return;

    if (DEBUG) devLog(`[PlanetDrag:Gesture] ✅ Long press complete! Activating drag for ${this.state.planet}`);

    this.state.isActive = true;

    // Remove indicator - overlay will provide feedback now
    this.removeIndicator();

    // Bring element to front
    if (this.state.element) {
      const parent = this.state.element.parentNode;
      if (parent) {
        parent.appendChild(this.state.element);
      }
    }

    // Read stepDegrees from chart settings
    const stepDegrees = this.getStepDegreesForPlanet(this.state.planet);

    // Emit start event
    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.gesture.start',
      'PlanetDragGestureHandler',
      {
        metadata: {
          chartId: this.chartId,
          planet: this.state.planet,
          startX: this.state.startX,
          startY: this.state.startY,
          stepDegrees,
        },
      }
    ));
  }

  /**
   * Get step degrees for a planet from chart settings
   */
  private getStepDegreesForPlanet(planet: string): number {
    const DEFAULT_STEP = 1;
    const settings = getLocalStorageItem<Record<string, any> | null>(`chart-settings-${this.chartId}`, null);
    return settings?.movementStepDegrees?.[planet] ?? DEFAULT_STEP;
  }

  private cancelLongPress() {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    this.cleanup();
  }

  // ===========================================================================
  // Visual Indicator
  // ===========================================================================

  private createIndicator(element: SVGGElement) {
    // Find the background circle of the planet to get its radius
    const bgCircle = element.querySelector('circle.planet-circle') || element.querySelector('circle');
    if (!bgCircle) {
      if (DEBUG) devLog(`[PlanetDrag:Gesture] ⚠️ No background circle found in`, element);
      return;
    }

    // Get radius from the background circle
    const bgRadius = parseFloat(bgCircle.getAttribute('r') || '15');
    const indicatorRadius = bgRadius + 5; // Slightly larger than planet

    // Get center position
    const cx = parseFloat(bgCircle.getAttribute('cx') || '0');
    const cy = parseFloat(bgCircle.getAttribute('cy') || '0');

    // Create indicator circle
    const indicator = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    indicator.setAttribute('cx', String(cx));
    indicator.setAttribute('cy', String(cy));
    indicator.setAttribute('r', String(indicatorRadius));
    indicator.setAttribute('fill', 'none');
    indicator.setAttribute('stroke', INDICATOR_COLOR);
    indicator.setAttribute('stroke-width', String(INDICATOR_STROKE_WIDTH));
    indicator.setAttribute('stroke-linecap', 'round');
    indicator.setAttribute('class', 'long-press-indicator');

    // Calculate stroke-dasharray for animation
    const circumference = 2 * Math.PI * indicatorRadius;
    indicator.setAttribute('stroke-dasharray', String(circumference));
    indicator.setAttribute('stroke-dashoffset', String(circumference));

    // Apply CSS animation
    indicator.style.transition = `stroke-dashoffset ${LONG_PRESS_DELAY_MS}ms linear`;
    indicator.style.transformOrigin = `${cx}px ${cy}px`;

    // Add to element
    element.appendChild(indicator);
    this.state.indicatorElement = indicator;

    // Trigger animation on next frame
    requestAnimationFrame(() => {
      indicator.setAttribute('stroke-dashoffset', '0');
    });

    if (DEBUG) devLog(`[PlanetDrag:Gesture] 🔵 Indicator created, radius: ${indicatorRadius}`);
  }

  private removeIndicator() {
    if (this.state.indicatorElement) {
      this.state.indicatorElement.remove();
      this.state.indicatorElement = null;
      if (DEBUG) devLog(`[PlanetDrag:Gesture] 🔵 Indicator removed`);
    }
  }

  // ===========================================================================
  // Cleanup
  // ===========================================================================

  private cleanup() {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    // Remove indicator
    this.removeIndicator();

    // Remove document listeners
    document.removeEventListener('pointermove', this.boundHandlers.pointerMove);
    document.removeEventListener('pointerup', this.boundHandlers.pointerUp);
    document.removeEventListener('pointercancel', this.boundHandlers.pointerCancel);

    // Reset state
    this.state = {
      isActive: false,
      planet: null,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      element: null,
      indicatorElement: null,
    };
  }

  /**
   * Destroy handler and remove all event listeners
   */
  destroy() {
    this.isDestroyed = true;
    this.cleanup();

    // Unsubscribe from all event subscriptions
    this.eventSubscriptions.forEach(sub => sub.unsubscribe());
    this.eventSubscriptions = [];

    // Remove handlers from sprites
    const sprites = this.getSprites();
    sprites.forEach((sprite) => {
      sprite.removeEventListener('pointerdown', this.boundHandlers.pointerDown);
    });
  }
}
